import React, { useState, useEffect } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import Moment from "moment";
import { BSBtn } from "traec-react/utils/bootstrap";
import { BreadCrumb } from "AppSrc/project/utils";
import { Spinner } from "traec-react/utils/entities";
import { BasicSpinner } from "storybook-dashboard/spinners/basicSpinner";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import Im from "immutable";
import Octicon from "react-octicon";
import { alertSuccess } from "traec-react/utils/sweetalert";
import { useFullIds } from "storybook-dashboard/utils/hooks";

// Reusable Card Component
const CompanyCard = ({ children, className = "", cardClassName = "mb-4" }) => {
  return (
    <div className="row">
      <div className="col-12">
        <div className={`card ${cardClassName}`}>{children}</div>
      </div>
    </div>
  );
};

// Header Component with title, breadcrumb, and navigation
const CompanyIndicatorHeader = ({ indicatorName, company, companyId }) => {
  return (
    <div className="mb-4">
      <h3 className="mb-3">{`Indicator Target: ${indicatorName}`}</h3>
      <BreadCrumb company={company} />
      <div className="mt-2">
        <Link to={`/company/${companyId}/indicators`} className="btn btn-outline-secondary btn-sm">
          <i className="fa fa-arrow-left mr-2"></i>
          Back to Indicators List
        </Link>
      </div>
    </div>
  );
};

// Target Configuration Component with performance direction and save functionality
const TargetConfiguration = ({ greenBelow, setGreenBelow, onSaveConfiguration, onToggleDirection, isSaving }) => {
  return (
    <CompanyCard>
      <div className="card-header">
        <h5 className="card-title mb-0">
          <Octicon name="settings" style={{ marginRight: "8px" }} />
          Target Configuration
        </h5>
      </div>
      <div className="card-body">
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label font-weight-bold">
              <Octicon name="graph" style={{ marginRight: "6px" }} />
              Performance Direction
            </label>
            <div className="mt-2">
              <p className="text-muted small mb-3">Desired performance above or below the targets and threshold?</p>
              <div className="btn-group" role="group">
                <BSBtn
                  extra_className="d-flex align-items-center"
                  noFloatRight={true}
                  primaryOff={!!greenBelow}
                  text={
                    <span>
                      <Octicon name="arrow-up" style={{ marginRight: "6px" }} />
                      Above Target
                    </span>
                  }
                  onClick={(e) => (onToggleDirection ? onToggleDirection(false) : setGreenBelow(false))}
                  disabled={isSaving}
                />
                <BSBtn
                  extra_className="d-flex align-items-center"
                  noFloatRight={true}
                  primaryOff={!greenBelow}
                  text={
                    <span>
                      <Octicon name="arrow-down" style={{ marginRight: "6px" }} />
                      Below Target
                    </span>
                  }
                  onClick={(e) => (onToggleDirection ? onToggleDirection(true) : setGreenBelow(true))}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6 d-flex align-items-end justify-content-end">
            <BSBtn
              extra_className="btn-success"
              noFloatRight={true}
              disabled={isSaving}
              text={
                isSaving ? (
                  <span>
                    <BasicSpinner />
                    <span className="ml-2">Saving...</span>
                  </span>
                ) : (
                  <span>
                    <Octicon name="check" style={{ marginRight: "6px" }} />
                    Save Configuration
                  </span>
                )
              }
              onClick={onSaveConfiguration}
            />
          </div>
        </div>
      </div>
    </CompanyCard>
  );
};

// Table Header Component for consistent table headers
const TableHeader = () => {
  return (
    <div className="row mb-3 py-2 bg-light rounded">
      <div className="col-md-4">
        <strong>Period / Level</strong>
      </div>
      <div className="col-md-3 text-center">
        <strong>Target Value</strong>
      </div>
      <div className="col-md-3 text-center">
        <strong>Threshold Value</strong>
      </div>
      <div className="col-md-2 text-center">
        <strong>Actions</strong>
      </div>
    </div>
  );
};

// Company Level Target Component for overall company targets input
const CompanyLevelTarget = ({
  target,
  setTarget,
  threshold,
  setThreshold,
  onDistributeValues,
  onTargetChange,
  onThresholdChange,
}) => {
  return (
    <div className="border-bottom mb-3 pb-3">
      <div className="row align-items-center">
        <div className="col-md-4">
          <div className="d-flex align-items-center">
            <Octicon name="organization" style={{ marginRight: "8px", color: "#007bff" }} />
            <div>
              <strong>Company Level</strong>
              <div className="small text-muted">Overall company targets</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group mb-0">
            <input
              className="form-control text-center"
              type="number"
              step="0.01"
              value={target}
              onChange={(e) => (onTargetChange ? onTargetChange(e.target.value) : setTarget(e.target.value))}
              placeholder="Target value"
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group mb-0">
            <input
              className="form-control text-center"
              type="number"
              step="0.01"
              value={threshold}
              onChange={(e) => (onThresholdChange ? onThresholdChange(e.target.value) : setThreshold(e.target.value))}
              placeholder="Threshold value"
            />
          </div>
        </div>
        <div className="col-md-2 text-center">
          <BSBtn
            extra_className="btn-outline-primary btn-sm"
            onClick={onDistributeValues}
            text={
              <span>
                <Octicon name="git-branch" style={{ marginRight: "4px" }} />
                Distribute
              </span>
            }
            title="Distribute values across all reporting periods"
          />
        </div>
      </div>
    </div>
  );
};

// Reporting Periods Section Component for period-specific targets
const ReportingPeriodsSection = ({ reportingPeriods, byDate, updateReportingPeriod }) => {
  return (
    <div className="mt-4">
      <h6 className="mb-3 text-muted">
        <Octicon name="calendar" style={{ marginRight: "6px" }} />
        Reporting Periods
      </h6>
      <ReportingPeriodRows
        reportingPeriods={reportingPeriods}
        byDate={byDate}
        updateReportingPeriod={updateReportingPeriod}
      />
    </div>
  );
};

// Target Values Table Component with company level and reporting periods
const TargetValuesTable = ({
  target,
  setTarget,
  threshold,
  setThreshold,
  reportingPeriods,
  byDate,
  updateReportingPeriod,
  onDistributeValues,
  onTargetChange,
  onThresholdChange,
}) => {
  return (
    <CompanyCard cardClassName="">
      <div className="card-header">
        <h5 className="card-title mb-0">
          <Octicon name="checklist" style={{ marginRight: "8px" }} />
          Target & Threshold Values
        </h5>
      </div>
      <div className="card-body">
        <TableHeader />

        <CompanyLevelTarget
          target={target}
          setTarget={setTarget}
          threshold={threshold}
          setThreshold={setThreshold}
          onDistributeValues={onDistributeValues}
          onTargetChange={onTargetChange}
          onThresholdChange={onThresholdChange}
        />

        <ReportingPeriodsSection
          reportingPeriods={reportingPeriods}
          byDate={byDate}
          updateReportingPeriod={updateReportingPeriod}
        />
      </div>
    </CompanyCard>
  );
};

// Helper function to update cumulative target
const updateCumulativeTarget = (
  e,
  companyId,
  metricTarget,
  baseMetricId,
  target,
  targets,
  greenBelow,
  threshold,
  setIsSaving,
  onTargetChange
) => {
  e.preventDefault();

  let url = `/api/company/${companyId}/target/`;
  let method = "POST";

  if (metricTarget?.size) {
    url = `/api/company/${companyId}/target/${metricTarget.get("uid")}/`;
    method = "PATCH";
  }

  doFetch(url, method, {
    metric: baseMetricId,
    value: target,
    date: Moment(),
    meta_json: {
      byDate: targets?.toJS(),
      greenBelow,
      thresholdLow: threshold,
    },
  })
    .then((response) => {
      setIsSaving(false);
      if (response.ok) {
        alertSuccess({
          text: `Thank you, the target has been updated`,
          onConfirm: () => {
            if (onTargetChange) onTargetChange();
          },
        });
      }
    })
    .catch(() => {
      setIsSaving(false);
    });
};

export function CompanyIndicatorTarget() {
  const history = useHistory();
  const { _companyId, indicatorId } = useParams();
  const { companyId } = useFullIds();
  const [toDate, setToDate] = useState("last");
  const [target, setTarget] = useState(0);
  const [threshold, setThreshold] = useState(0);
  const [greenBelow, setGreenBelow] = useState(true);
  const [byDate, setByDate] = useState(Im.List());
  const [targetDidLoad, setTargetDidLoad] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch company data
  const { data: company } = useApi("/api/company/{_companyId}/", { _companyId });

  // Fetch company targets
  const { data: companyTargets, trigger: triggerTargets } = useApi("/api/company/{companyId}/target/", { companyId });
  const metricTargetMap = (companyTargets || Im.List()).reduce(
    (map, target) => map.set(target.getIn(["metric", "uid"]), target),
    Im.Map()
  );

  // Fetch company indicators
  const { data: companyIndicators, trigger: triggerIndicators } = useApi("/api/company/{companyId}/indicator/", {
    companyId,
  });
  const indicator = companyIndicators?.find((i) => i.get("uid") === indicatorId);
  const baseMetric = indicator?.get("resultBaseMetric");
  const baseMetricId = baseMetric?.get("uid");
  const metricTarget = metricTargetMap.get(baseMetricId);

  // Fetch reporting periods
  const { data: reportingPeriods } = useApi("/api/company/{companyId}/report/?ignore_summary=true&to_date={toDate}", {
    companyId,
    toDate,
  });

  const indicatorName = indicator?.get("name") || "Indicator";

  useEffect(() => {
    if (metricTarget && metricTarget.size && !targetDidLoad) {
      setTarget(metricTarget.get("value") || 0);
      setTargetDidLoad(true);
      setByDate(metricTarget.getIn(["meta_json", "byDate"]) || Im.List());
      setThreshold(getThresholdFromTarget(metricTarget) || 0);
      setGreenBelow(metricTarget.getIn(["meta_json", "greenBelow"]) ?? true);
    }
  }, [metricTarget, targetDidLoad]);

  const getThresholdFromTarget = (target) => {
    if (target.getIn(["meta_json", "thresholdLow"])) {
      return target.getIn(["meta_json", "thresholdLow"]);
    } else if (target.getIn(["meta_json", "thresholdHigh"])) {
      return target.getIn(["meta_json", "thresholdHigh"]);
    } else {
      return null;
    }
  };

  const updateReportingPeriod = (e, reportingPeriod, targetType) => {
    e.preventDefault();
    let startDate = new Moment(reportingPeriod.get("startDate")).format("YYYY-MM-DD");
    let obj = byDate.filter((i) => i.get("startDate") == startDate).first();
    let newByDate = byDate
      .filter((i) => i.get("startDate") != startDate)
      .push(obj.set(targetType, parseFloat(e.target.value)));
    setByDate(newByDate);
  };

  const distributeValues = (e) => {
    e.preventDefault();
    let num_reportingPeriods = reportingPeriods?.size || 0;
    let sortedPeriods = reportingPeriods?.sortBy((reportingPeriod) => reportingPeriod.get("startDate"));
    let runningTarget = 0;
    let runningThreshold = 0;

    let targetsByDate = sortedPeriods?.toList().map((reportingPeriod) => {
      runningTarget = runningTarget + (target / num_reportingPeriods ? target / num_reportingPeriods : 0);
      runningThreshold = runningThreshold + (threshold / num_reportingPeriods ? threshold / num_reportingPeriods : 0);
      return Im.fromJS({
        target: runningTarget,
        threshold: runningThreshold,
        startDate: new Moment(reportingPeriod.get("startDate")).format("YYYY-MM-DD"),
        endDate: new Moment(reportingPeriod.get("endDate")).format("YYYY-MM-DD"),
      });
    });

    console.log("Updating targets by date", targetsByDate?.toJS());
    setByDate(targetsByDate);
  };

  // Handle target value changes
  const handleTargetChange = (value) => {
    setTarget(value);
  };

  // Handle threshold value changes with automatic direction adjustment
  const handleThresholdChange = (value) => {
    const thresholdNum = Number(value);
    const targetNum = Number(target);

    // Only auto-adjust if both values are valid numbers and target is not zero
    if (!isNaN(thresholdNum) && !isNaN(targetNum) && targetNum !== 0 && value !== "") {
      // If threshold is greater than target, performance should be "Below Target" (greenBelow = true)
      // If threshold is less than target, performance should be "Above Target" (greenBelow = false)
      if (thresholdNum > targetNum) {
        setGreenBelow(true);
      } else if (thresholdNum < targetNum) {
        setGreenBelow(false);
      }
    }

    setThreshold(value);
  };

  // Handle manual toggle of performance direction
  const handleToggleDirection = (newGreenBelow) => {
    const thresholdNum = Number(threshold);
    const targetNum = Number(target);

    // Check if threshold is set and valid
    if (threshold !== "" && threshold !== 0 && !isNaN(thresholdNum) && !isNaN(targetNum) && targetNum !== 0) {
      // Check if the direction matches the threshold-target relationship
      const thresholdIsAboveTarget = thresholdNum > targetNum;
      const thresholdIsBelowTarget = thresholdNum < targetNum;

      // If switching to "Below Target" (greenBelow = true), threshold must be > target
      // If switching to "Above Target" (greenBelow = false), threshold must be < target
      if (newGreenBelow && thresholdIsBelowTarget) {
        // Invalid: trying to set "Below Target" but threshold is below target
        alert(
          "Performance direction changed to 'Below Target'.\n\n" +
            "The threshold value has been cleared because it was less than the target value.\n\n" +
            "Please enter a threshold value that is greater than the target value."
        );
        setThreshold("");
      } else if (!newGreenBelow && thresholdIsAboveTarget) {
        // Invalid: trying to set "Above Target" but threshold is above target
        alert(
          "Performance direction changed to 'Above Target'.\n\n" +
            "The threshold value has been cleared because it was greater than the target value.\n\n" +
            "Please enter a threshold value that is less than the target value."
        );
        setThreshold("");
      }
    }

    setGreenBelow(newGreenBelow);
  };

  const onTargetChange = () => {
    triggerTargets();
    history.push(`/company/${companyId}/indicators`);
  };

  const handleSaveConfiguration = (e) => {
    setIsSaving(true);
    updateCumulativeTarget(
      e,
      companyId,
      metricTarget,
      baseMetricId,
      target,
      byDate,
      greenBelow,
      threshold,
      setIsSaving,
      onTargetChange
    );
  };

  if (!(indicator && company)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <BasicSpinner />
          <p className="text-muted mt-3">Loading indicator and target data, please be patient.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <CompanyIndicatorHeader indicatorName={indicatorName} company={company} companyId={companyId} />

      <TargetConfiguration
        greenBelow={greenBelow}
        setGreenBelow={setGreenBelow}
        onSaveConfiguration={handleSaveConfiguration}
        onToggleDirection={handleToggleDirection}
        isSaving={isSaving}
      />

      <TargetValuesTable
        target={target}
        setTarget={setTarget}
        threshold={threshold}
        setThreshold={setThreshold}
        reportingPeriods={reportingPeriods}
        byDate={byDate}
        updateReportingPeriod={updateReportingPeriod}
        onDistributeValues={distributeValues}
        onTargetChange={handleTargetChange}
        onThresholdChange={handleThresholdChange}
      />
    </div>
  );
}

export default CompanyIndicatorTarget;

// Reporting Period Rows Component
function ReportingPeriodRows({ reportingPeriods, byDate, updateReportingPeriod }) {
  if (!reportingPeriods) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="text-center">
          <Spinner explanation="Loading Reporting Periods" timedOutComment="Could not load Reporting Periods" />
        </div>
      </div>
    );
  }

  let sortedPeriods = reportingPeriods.sortBy((reportingPeriod) => reportingPeriod.get("startDate")).valueSeq();

  if (sortedPeriods.size === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-muted">
          <Octicon name="calendar" style={{ fontSize: "48px", marginBottom: "16px", display: "block" }} />
          <h6>No Reporting Periods Found</h6>
          <p className="small">Please configure reporting periods for this company first.</p>
        </div>
      </div>
    );
  }

  let targetMap = Im.Map();
  try {
    targetMap = byDate.reduce((a, c) => a.set(c.get("startDate"), c), Im.Map());
  } catch (err) {
    console.warn("Error producing targetMap", err);
    targetMap = Im.Map();
  }

  return (
    <div>
      {sortedPeriods.map((reportingPeriod, index) => {
        let key = new Moment(reportingPeriod.get("startDate")).format("YYYY-MM-DD");
        return (
          <ReportingPeriodRow
            key={index}
            reportingPeriod={reportingPeriod}
            target={targetMap.get(key)?.get("target") || 0}
            threshold={targetMap.get(key)?.get("threshold") || 0}
            updateReportingPeriod={updateReportingPeriod}
          />
        );
      })}
    </div>
  );
}

// Reporting Period Row Component
const ReportingPeriodRow = ({ reportingPeriod, target, threshold, updateReportingPeriod }) => {
  const formatDate = (date) => Moment(date).format("Do MMM YY");

  return (
    <div className="row align-items-center py-2 border-bottom">
      <div className="col-md-4">
        <div className="d-flex align-items-center">
          <Octicon name="calendar" style={{ marginRight: "8px", color: "#6c757d" }} />
          <div>
            <strong>
              {formatDate(reportingPeriod.get("startDate"))} - {formatDate(reportingPeriod.get("endDate"))}
            </strong>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-group mb-0">
          <input
            className="form-control text-center"
            type="number"
            step="0.01"
            placeholder={target.toFixed(2)}
            onChange={(e) => updateReportingPeriod(e, reportingPeriod, "target")}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-group mb-0">
          <input
            className="form-control text-center"
            type="number"
            step="0.01"
            placeholder={threshold.toFixed(2)}
            onChange={(e) => updateReportingPeriod(e, reportingPeriod, "threshold")}
          />
        </div>
      </div>
      <div className="col-md-2 text-center">
        <small className="text-muted">
          <Octicon name="dash" style={{ marginRight: "4px" }} />
          Period
        </small>
      </div>
    </div>
  );
};
